import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProcessFlow from '@/components/HowItWorks/ProcessFlow';
import { useModal } from '@/contexts';
import TagBar from '@/components/common/TagBar';
import { useI18n } from '@/i18n';

const HowItWorksPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [activeAudience, setActiveAudience] = useState<'parent' | 'tutor'>(
    searchParams.get('tab') === 'tutor' ? 'tutor' : 'parent'
  );
  const { openTutorRegistration, openParentRegistration } = useModal();
  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary to-secondary text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('howItWorks.hero.title')}</h1>
          <p className="text-xl max-w-3xl mx-auto text-gray-100">
            {t('howItWorks.hero.description')}
          </p>
        </div>
      </section>

      {/* Audience Tabs */}
      <section className="bg-white shadow-md sticky top-16 z-40">
        <div className="container mx-auto px-4">
          <TagBar
            items={[
              { id: 'parent', label: t('howItWorks.tab.parents') },
              { id: 'tutor', label: t('howItWorks.tab.tutors') },
            ]}
            activeId={activeAudience}
            onChange={setActiveAudience}
            innerClassName="flex justify-center space-x-1 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0"
          />
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <div className="text-[11px] tracking-[.22em] uppercase text-accent font-bold mb-3">
              {t('howItWorks.journey.eyebrow')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-3">
              {activeAudience === 'parent'
                ? t('howItWorks.journey.parent.title')
                : t('howItWorks.journey.tutor.title')}
            </h2>
            <p className="text-base text-gray-600 max-w-[520px] mx-auto leading-relaxed">
              {activeAudience === 'parent'
                ? t('howItWorks.journey.parent.description')
                : t('howItWorks.journey.tutor.description')}
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <ProcessFlow audience={activeAudience} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-primary text-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            {activeAudience === 'tutor' ? (
              <>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">{t('howItWorks.cta.tutor.title')}</h2>
                <p className="text-xl mb-8 text-gray-100">
                  {t('howItWorks.cta.tutor.description')}
                </p>
                <button
                  onClick={() => openTutorRegistration()}
                  className="btn-secondary inline-block px-8 py-4 text-lg font-semibold"
                >
                  {t('howItWorks.cta.tutor.button')}
                </button>
              </>
            ) : (
              <>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">{t('howItWorks.cta.parent.title')}</h2>
                <p className="text-xl mb-8 text-gray-100">
                  {t('howItWorks.cta.parent.description')}
                </p>
                <button
                  onClick={() => openParentRegistration()}
                  className="btn-secondary inline-block px-8 py-4 text-lg font-semibold"
                >
                  {t('howItWorks.cta.parent.button')}
                </button>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
